/**
 * Shared 401 / 403 response interceptor factory.
 *
 * Attach to any axios instance (global `axios` or `adminApi`) to get:
 *  - Silent token refresh on 401 "Please authenticate."
 *  - Concurrent-request queuing so only one refresh call is made at a time
 *  - Automatic forceLogout() when the refresh itself fails
 *  - 403 redirect to the permission-denied page
 *
 * The refresh POST always uses the base `axios` (not the decorated instance)
 * so it bypasses the interceptor and cannot cause an infinite loop.
 */
import axios from 'axios';
import { getCookies, setCookies } from '../hooks/useCookies';
import { SERVER_BASE_URL } from '../redux/apiConstants';
import { forceLogout } from './authUtils';

let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error, token) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

export const attach401Interceptor = (instance) => {
  instance.interceptors.response.use(
    (res) => {
      if (res.status === 403) {
        window.location.assign('/permission');
      }
      return res;
    },
    async (err) => {
      // Network error or request cancelled — no response to inspect
      if (!err.response) return Promise.reject(err);

      const { status, data } = err.response;
      const originalConfig = err.config;

      if (status === 403) {
        // Stay on the admin frontend — env REACT_APP_API_PERMISSION_DENIED was pointing at the API host.
        window.location.assign('/permission');
        return Promise.reject(err);
      }

      const shouldRefresh =
        status === 401 &&
        data?.message === 'Please authenticate.' &&
        getCookies('Token') &&
        !originalConfig._retry;

      if (!shouldRefresh) return Promise.reject(err);

      // Another request is already refreshing — queue this one and wait
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalConfig);
          })
          .catch((e) => Promise.reject(e));
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        // Use the bare axios instance so this call is NOT intercepted
        const rs = await axios.post(
          `${SERVER_BASE_URL}/auth/refresh-token`,
          { refreshToken: getCookies('refreshToken') },
          { headers: { 'Content-Type': 'application/json; charset=utf-8' } }
        );

        const newAccessToken = rs.data.data.access.token;
        const newRefreshToken = rs.data.data.refresh.token;

        setCookies('Token', newAccessToken);
        setCookies('refreshToken', newRefreshToken);
        axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;

        flushQueue(null, newAccessToken);
        return instance(originalConfig);
      } catch (refreshError) {
        flushQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};
